import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, User, Lock, Globe, Eye, EyeOff, Check, AlertCircle, RefreshCw, Bell } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import i18n from "@/i18n";

const API = `${process.env.REACT_APP_API_URL}/api`;

const PLAN_COLORS = { free: "text-muted-foreground", starter: "text-emerald-400", pro: "text-brand-primary", business: "text-purple-400" };
const PLAN_LABELS = { free: "Free", starter: "Starter", pro: "Pro", business: "Business" };

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();

  const [name, setName]         = useState(user?.name || "");
  const [picture, setPicture]   = useState(user?.picture || "");
  const [language, setLanguage] = useState(i18n.language?.startsWith("en") ? "en" : "id");
  const [savingProfile, setSavingProfile] = useState(false);

  const [oldPw, setOldPw]         = useState("");
  const [newPw, setNewPw]         = useState("");
  const [showOld, setShowOld]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [savingPw, setSavingPw]   = useState(false);
  const [trainingConsent, setTrainingConsent] = useState(!!user?.training_consent);
  const [savingTrainingConsent, setSavingTrainingConsent] = useState(false);

  const [autoRecharge, setAutoRecharge] = useState(user?.auto_recharge_enabled || false);
  const [rechargeType, setRechargeType] = useState(user?.auto_recharge_type || "package");
  const [rechargePack, setRechargePack] = useState(user?.auto_recharge_pack_id || "pack_10");
  const [customAmount, setCustomAmount] = useState(user?.auto_recharge_custom_amount || "");
  const [savingRecharge, setSavingRecharge] = useState(false);

  const CREDIT_PACKS = [
    { id: "pack_10", credits: 10, price: 20000 },
    { id: "pack_25", credits: 25, price: 45000 },
    { id: "pack_50", credits: 50, price: 80000 },
  ];

  const isGoogleUser = !user?.password_hash && user?.auth_type === "google";
  const { permission, subscribed, requestPermission, unsubscribe } = usePushNotifications(user);

  const handleEnableNotifications = async () => {
    const result = await requestPermission();
    if (result === "granted") {
      toast.success(t("profile.notifications.enabled"));
    } else if (result === "denied") {
      toast.error(t("profile.notifications.blocked"));
    }
  };

  const handleDisableNotifications = async () => {
    const ok = await unsubscribe();
    if (ok) toast.success(t("profile.notifications.disabled"));
  };

  const handleSaveTrainingConsent = async () => {
    setSavingTrainingConsent(true);
    try {
      const res = await axios.post(
        `${API}/auth/training-consent`,
        { consent: trainingConsent, source: "profile" },
        { withCredentials: true }
      );
      setUser({
        ...user,
        training_consent: !!res.data?.training_consent,
        training_consent_updated_at: res.data?.training_consent_updated_at || null,
      });
      toast.success(
        trainingConsent ? t("profile.training.on") : t("profile.training.off")
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || t("profile.training.error"));
    } finally {
      setSavingTrainingConsent(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await axios.patch(
        `${API}/auth/profile`,
        { name: name.trim(), language, picture },
        { withCredentials: true }
      );
      setUser({ ...user, ...res.data });
      i18n.changeLanguage(language);
      toast.success(t("profile.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error(t("register.passwordHint")); return; }
    setSavingPw(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { old_password: oldPw, new_password: newPw },
        { withCredentials: true }
      );
      toast.success(t("profile.password.success"));
      setOldPw(""); setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSavingPw(false);
    }
  };

  const handleAutoRecharge = async (enabled) => {
    setSavingRecharge(true);
    try {
      const payload = { enabled };
      if (enabled) {
        payload.recharge_type = rechargeType;
        if (rechargeType === "package") {
          payload.pack_id = rechargePack;
        } else {
          const amt = parseInt(customAmount, 10);
          if (!amt || amt < 10000) {
            toast.error(t("profile.recharge.nominalError"));
            setSavingRecharge(false);
            return;
          }
          payload.custom_amount = amt;
        }
      }
      await axios.post(
        `${API}/auth/auto-recharge`,
        payload,
        { withCredentials: true }
      );
      setAutoRecharge(enabled);
      setUser({ ...user, auto_recharge_enabled: enabled, auto_recharge_type: rechargeType });
      toast.success(enabled ? t("profile.recharge.on") : t("profile.recharge.off"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("profile.recharge.error"));
    } finally {
      setSavingRecharge(false);
    }
  };

  const joinedDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(language === "id" ? "id-ID" : "en-US", { month: "long", year: "numeric" })
    : "—";
  const trainingUpdatedAt = user?.training_consent_updated_at
    ? new Date(user.training_consent_updated_at).toLocaleString(language === "id" ? "id-ID" : "en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <main className="min-h-screen" data-testid="profile-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Back */}
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          {t("nav.dashboard")}
        </Link>

        {/* Page Header */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("profile.plan")}</span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">{t("profile.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("profile.subtitle")}</p>
        </motion.div>

        {/* User Summary Card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="flex items-center gap-4 p-5 rounded-md border border-border bg-card mb-6">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full object-cover border border-border" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-brand-primary/20 border border-brand-primary/30 flex items-center justify-center">
              <span className="font-heading font-bold text-xl text-brand-primary">{initials}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-heading font-semibold text-lg truncate">{user?.name}</p>
            <p className="text-sm font-mono text-muted-foreground truncate">{user?.email}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-xs font-mono font-semibold ${PLAN_COLORS[user?.plan || "free"]}`}>
                {PLAN_LABELS[user?.plan || "free"]} Plan
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{t("profile.member.since")} {joinedDate}</span>
            </div>
          </div>
        </motion.div>

        {/* Profile Form */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-md border border-border bg-card p-6 mb-4">
          <div className="flex items-center gap-2 mb-5">
            <User className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">{t("profile.name")} & {t("profile.language")}</h2>
          </div>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("profile.name")}</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nama Lengkap"
                className="rounded-sm text-sm"
                data-testid="profile-name-input"
              />
            </div>
            {/* Email (readonly) */}
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("profile.email")}</label>
              <Input
                value={user?.email || ""}
                disabled
                className="rounded-sm text-sm opacity-60 cursor-not-allowed"
                data-testid="profile-email-input"
              />
              <p className="text-xs text-muted-foreground">{t("profile.email.hint")}</p>
            </div>
            {/* Picture URL */}
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("profile.picture")}</label>
              <Input
                value={picture}
                onChange={(e) => setPicture(e.target.value)}
                placeholder="https://example.com/photo.jpg"
                className="rounded-sm text-sm"
                data-testid="profile-picture-input"
              />
              <p className="text-xs text-muted-foreground">{t("profile.picture.hint")}</p>
            </div>
            {/* Language */}
            <div className="space-y-1.5">
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("profile.language")}</label>
              <div className="flex gap-2" data-testid="language-selector">
                {[
                  { code: "id", label: t("profile.language.id") },
                  { code: "en", label: t("profile.language.en") },
                ].map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLanguage(code)}
                    data-testid={`lang-${code}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-sm border text-sm font-medium transition-all ${
                      language === code
                        ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                        : "border-border hover:border-brand-primary/40 text-muted-foreground"
                    }`}
                  >
                    {language === code && <Check className="w-3.5 h-3.5" />}
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <Button
              type="submit"
              disabled={savingProfile}
              className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium"
              data-testid="save-profile-btn"
            >
              {savingProfile ? t("common.loading") : t("profile.save")}
            </Button>
          </form>
        </motion.div>

        {/* Password Form */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="rounded-md border border-border bg-card p-6 mb-4">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">{t("profile.password.title")}</h2>
          </div>
          {isGoogleUser ? (
            <div className="flex items-start gap-2 p-3 rounded-sm bg-secondary/50 border border-border">
              <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">{t("profile.google.hint")}</p>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("profile.password.old")}</label>
                <div className="relative">
                  <Input
                    type={showOld ? "text" : "password"}
                    value={oldPw}
                    onChange={(e) => setOldPw(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="rounded-sm text-sm pr-9"
                    data-testid="old-password-input"
                  />
                  <button type="button" onClick={() => setShowOld(!showOld)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showOld ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{t("profile.password.new")}</label>
                <div className="relative">
                  <Input
                    type={showNew ? "text" : "password"}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    className="rounded-sm text-sm pr-9"
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">{t("profile.password.hint")}</p>
              </div>
              <Button
                type="submit"
                disabled={savingPw}
                variant="outline"
                className="rounded-md font-medium"
                data-testid="save-password-btn"
              >
                {savingPw ? t("common.loading") : t("profile.password.save")}
              </Button>
            </form>
          )}
        </motion.div>

        {/* Notifications */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
          className="rounded-md border border-border bg-card p-6 mb-4" data-testid="notifications-section">
          <div className="flex items-center gap-2 mb-5">
            <Bell className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">{t("profile.notifications.title")}</h2>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-1 pr-4">
              <p className="text-sm text-foreground">{t("profile.notifications.label")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("profile.notifications.desc")}</p>
            </div>
            {permission === "granted" ? (
              <div className="flex items-center gap-3" data-testid="notifications-status">
                {subscribed ? (
                  <>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-verdict-real/10 text-verdict-real">
                      <Check className="w-3.5 h-3.5" />
                      <span className="text-xs font-mono font-medium">{t("profile.notifications.active")}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm text-xs h-8"
                      onClick={handleDisableNotifications}
                      data-testid="disable-notifications-btn"
                    >
                      {t("profile.notifications.disable")}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    className="rounded-sm bg-brand-primary text-white text-xs"
                    onClick={handleEnableNotifications}
                    data-testid="enable-notifications-btn"
                  >
                    {t("profile.notifications.enable")}
                  </Button>
                )}
              </div>
            ) : permission === "denied" ? (
              <div className="text-right" data-testid="notifications-status">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-verdict-fake/10 text-verdict-fake mb-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span className="text-xs font-mono font-medium">{t("profile.notifications.denied")}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">{t("profile.notifications.deniedHint")}</p>
              </div>
            ) : (
              <Button
                size="sm"
                className="rounded-sm bg-brand-primary text-white text-xs"
                onClick={handleEnableNotifications}
                data-testid="enable-notifications-btn"
              >
                {t("profile.notifications.enable")}
              </Button>
            )}
          </div>
        </motion.div>

        {/* AI Training Consent */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.19 }}
          className="rounded-md border border-border bg-card p-6 mb-4" data-testid="training-consent-section">
          <div className="flex items-center gap-2 mb-5">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">{t("profile.training.title")}</h2>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 pr-4">
              <p className="text-sm text-foreground">{t("profile.training.label")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("profile.training.desc")}</p>
              {trainingUpdatedAt && (
                <p className="text-[11px] text-muted-foreground mt-2 font-mono">
                  {t("profile.training.updatedAt", { date: trainingUpdatedAt })}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setTrainingConsent((prev) => !prev)}
              disabled={savingTrainingConsent}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                trainingConsent ? "bg-brand-primary" : "bg-secondary"
              }`}
              data-testid="training-consent-toggle"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                trainingConsent ? "translate-x-6" : "translate-x-0.5"
              }`} />
            </button>
          </div>
          <div className="mt-4">
            <Button
              type="button"
              onClick={handleSaveTrainingConsent}
              disabled={savingTrainingConsent}
              variant="outline"
              className="rounded-sm text-sm"
              data-testid="save-training-consent-btn"
            >
              {savingTrainingConsent ? t("profile.training.saving") : t("profile.training.save")}
            </Button>
          </div>
        </motion.div>

        {/* Auto-Recharge */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="rounded-md border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-5">
            <RefreshCw className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-heading font-semibold">{t("profile.recharge.title")}</h2>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-1 pr-4">
              <p className="text-sm text-foreground">{t("profile.recharge.label")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("profile.recharge.desc")}</p>
            </div>
            <button
              onClick={() => {
                if (autoRecharge) {
                  handleAutoRecharge(false);
                } else {
                  setAutoRecharge(true);
                }
              }}
              disabled={savingRecharge}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                autoRecharge ? "bg-brand-primary" : "bg-secondary"
              }`}
              data-testid="auto-recharge-toggle"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                autoRecharge ? "translate-x-6" : "translate-x-0.5"
              }`} />
            </button>
          </div>

          {autoRecharge && (
            <div className="mt-5 space-y-4">
              {/* Recharge Type Selection */}
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">{t("profile.recharge.typeLabel")}</label>
                <div className="flex gap-2">
                  {[
                    { type: "package", label: t("profile.recharge.package") },
                    { type: "custom", label: t("profile.recharge.custom") },
                  ].map(({ type, label }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setRechargeType(type)}
                      className={`px-4 py-2 rounded-sm border text-sm font-medium transition-all ${
                        rechargeType === type
                          ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                          : "border-border text-muted-foreground hover:border-brand-primary/40"
                      }`}
                      data-testid={`recharge-type-${type}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Package Selection */}
              {rechargeType === "package" && (
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">{t("profile.recharge.selectPack")}</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="recharge-packages">
                    {CREDIT_PACKS.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setRechargePack(p.id)}
                        className={`p-3 rounded-sm border text-center transition-all ${
                          rechargePack === p.id
                            ? "border-brand-primary bg-brand-primary/10"
                            : "border-border hover:border-brand-primary/40"
                        }`}
                        data-testid={`pack-${p.id}`}
                      >
                        <div className={`text-lg font-bold ${rechargePack === p.id ? "text-brand-primary" : "text-foreground"}`}>{p.credits}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{t("profile.recharge.credits")}</div>
                        <div className={`text-xs font-semibold mt-1 ${rechargePack === p.id ? "text-brand-primary" : "text-muted-foreground"}`}>
                          Rp {p.price.toLocaleString("id-ID")}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Amount Input */}
              {rechargeType === "custom" && (
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">{t("profile.recharge.nominalLabel")}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-mono">Rp</span>
                    <Input
                      type="number"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="50000"
                      min={10000}
                      max={1000000}
                      className="pl-10 rounded-sm text-sm font-mono"
                      data-testid="custom-amount-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t("profile.recharge.nominalMin")}</p>
                </div>
              )}

              {/* Save Button */}
              <Button
                onClick={() => handleAutoRecharge(true)}
                disabled={savingRecharge}
                className="rounded-sm bg-brand-primary text-white text-sm"
                data-testid="save-auto-recharge-btn"
              >
                {savingRecharge ? t("profile.recharge.saving") : t("profile.recharge.save")}
              </Button>

              <div className="p-3 rounded-sm bg-brand-primary/5 border border-brand-primary/20">
                <p className="text-xs text-brand-primary font-mono">
                  {rechargeType === "package"
                    ? `Auto-recharge: ${CREDIT_PACKS.find(p => p.id === rechargePack)?.credits || 10} kredit (Rp ${(CREDIT_PACKS.find(p => p.id === rechargePack)?.price || 20000).toLocaleString("id-ID")}) saat kuota habis.`
                    : `Auto-recharge: Rp ${parseInt(customAmount || 0, 10).toLocaleString("id-ID")} saat kuota habis.`
                  }
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
}
